<template>
  <div id="dialog-curriculumList">
    
    <!-- 레슨 뷰어 -->
    <CurriculumView 
      v-if="viewerVisible" 
      :lesson="selectedLesson"
      :allLessons="allLessonsFlat"
      :windowOnOff="closeViewer"
      :onProgressUpdate="handleProgressUpdate"
    />

    <div class="curriculum-list">

      <div class="header">
        <p @click="windowOnOff(false)"><i class="ph-bold ph-arrow-left"></i></p>
        <h2>커리큘럼</h2>
        <p v-if="isAdmin" @click="showAdmin = true"><i class="ph-bold ph-gear"></i></p>
        <p v-else style="opacity:0;"><i class="ph-bold ph-dots-three"></i></p>
      </div>

      <!-- 관리자 페이지 -->
      <CurriculumAdmin v-if="showAdmin" :windowOnOff="(b) => showAdmin = b" />
      <!-- 수강 현황 페이지 -->
      <CurriculumHistory v-if="showHistory" :windowOnOff="(b) => showHistory = b" />

      <!-- 전체 진도 -->
      <div class="overall-progress">
        <div class="progress-info">
          <span class="progress-label">전체 진도</span>
          <span class="progress-value">{{ overallProgress }}%</span>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: overallProgress + '%' }"></div>
        </div>
        <div class="progress-stats">
          <span>{{ completedCount }}/{{ totalLessons }} 완료</span>
        </div>
      </div>

      <div class="content">
        <!-- 카테고리 아코디언 -->
        <div class="category-list">
          <div 
            v-for="category in treeData" 
            :key="category.idx" 
            class="category-section"
          >
            <div 
              class="category-header" 
              @click="toggleCategory(category.idx)"
            >
              <div class="category-info">
                <i :class="expanded[category.idx] ? 'ph ph-folder-open' : 'ph ph-folder'"></i>
                <span class="category-title">{{ category.title }}</span>
              </div>
              <div class="category-meta">
                <span class="category-progress">{{ getCategoryProgress(category) }}%</span>
                <i :class="expanded[category.idx] ? 'ph ph-caret-up' : 'ph ph-caret-down'"></i>
              </div>
            </div>

            <!-- 챕터 목록 -->
            <div v-show="expanded[category.idx]" class="chapter-list">
              <div 
                v-for="chapter in category.children" 
                :key="chapter.idx" 
                class="chapter-section"
              >
                <div 
                  class="chapter-header"
                  @click="toggleChapter(chapter.idx)"
                >
                  <div class="chapter-info">
                    <span class="chapter-number">{{ getChapterNumber(category, chapter) }}</span>
                    <span class="chapter-title">{{ chapter.title }}</span>
                  </div>
                  <div class="chapter-meta">
                    <span class="chapter-count">
                      {{ getCompletedLessons(chapter) }}/{{ chapter.children?.length || 0 }}
                    </span>
                    <i :class="expandedChapter[chapter.idx] ? 'ph ph-caret-up' : 'ph ph-caret-down'"></i>
                  </div>
                </div>

                <!-- 레슨 목록 -->
                <div v-show="expandedChapter[chapter.idx]" class="lesson-list">
                  <div 
                    v-for="lesson in chapter.children" 
                    :key="lesson.idx"
                    class="lesson-item"
                    :class="{ 
                      'completed': getLessonStatus(lesson.idx) === 'completed',
                      'in-progress': getLessonStatus(lesson.idx) === 'in_progress'
                    }"
                    @click="openLesson(lesson)"
                  >
                    <div class="lesson-status-icon">
                      <i v-if="getLessonStatus(lesson.idx) === 'completed'" class="ph-fill ph-check-circle"></i>
                      <i v-else-if="getLessonStatus(lesson.idx) === 'in_progress'" class="ph ph-circle-half"></i>
                      <i v-else class="ph ph-circle"></i>
                    </div>
                    <div class="lesson-info">
                      <div class="lesson-title-row">
                        <i :class="lesson.content_type === 'video' ? 'ph ph-play-circle' : 'ph ph-article'" class="type-icon"></i>
                        <span class="lesson-title">{{ lesson.title }}</span>
                      </div>
                      <div class="lesson-meta">
                        <span v-if="lesson.duration" class="lesson-duration">
                          <i class="ph ph-clock"></i>
                          {{ formatDuration(lesson.duration) }}
                        </span>
                        <span v-if="getLessonProgress(lesson.idx) > 0" class="lesson-progress-text">
                          {{ getLessonProgress(lesson.idx) }}% 완료
                        </span>
                      </div>
                    </div>
                    <i class="ph ph-caret-right lesson-arrow"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 빈 상태 -->
          <div v-if="treeData.length === 0" class="empty-state">
            <i class="ph ph-book-open"></i>
            <p>준비된 커리큘럼이 없습니다</p>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>


<script>
import { inject, ref, reactive, computed, onMounted } from "vue"
import { useBackButton } from '@/utils/useBackBtn'
import CurriculumView from './CurriculumView.vue'
import CurriculumAdmin from './CurriculumAdmin.vue'
import CurriculumHistory from './CurriculumHistory.vue'

export default {
  components: {
    CurriculumView,
    CurriculumAdmin,
    CurriculumHistory
  },
  props: {
    windowOnOff: Function,
  },
  setup(props) {
    const sql_query = inject('sql_query')
    const getMbid = inject('getMbid')
    const member_info = inject('member_info')

    useBackButton(() => {
      if (viewerVisible.value) {
        closeViewer()
      } else if (showAdmin.value) {
        showAdmin.value = false
      } else if (showHistory.value) {
        showHistory.value = false
      } else {
        props.windowOnOff(false)
      }
    })

    // 관리자 여부 (level >= 1)
    const isAdmin = computed(() => member_info?.level >= 1)
    const showAdmin = ref(false)
    const showHistory = ref(false)

    const treeData = ref([])
    const expanded = reactive({})
    const expandedChapter = reactive({})
    const progressMap = reactive({})

    const viewerVisible = ref(false)
    const selectedLesson = ref(null)
    const allLessonsFlat = ref([])

    async function fetchData() {
      const items = await sql_query(`
        SELECT * FROM curriculum_items 
        WHERE is_active = 1
        ORDER BY sort_order ASC
      `)

      const categories = items.filter(i => i.type === 'category')
      const chapters = items.filter(i => i.type === 'chapter')
      const lessons = items.filter(i => i.type === 'lesson')

      allLessonsFlat.value = []

      treeData.value = categories.map(cat => {
        const catChapters = chapters
          .filter(ch => ch.parent_idx === cat.idx)
          .map(ch => {
            const chLessons = lessons.filter(ls => ls.parent_idx === ch.idx)
            allLessonsFlat.value.push(...chLessons)
            return { ...ch, children: chLessons }
          })
        return { ...cat, children: catChapters }
      })

      if (categories.length > 0) {
        expanded[categories[0].idx] = true
        if (treeData.value[0]?.children?.length > 0) {
          expandedChapter[treeData.value[0].children[0].idx] = true
        }
      }

      await fetchProgress()
    }

    async function fetchProgress() {
      const mbId = getMbid()
      const progress = await sql_query(`
        SELECT lesson_idx, status, progress 
        FROM user_lesson_progress 
        WHERE mb_id = '${mbId}'
      `)

      progress.forEach(p => {
        progressMap[p.lesson_idx] = {
          status: p.status,
          progress: p.progress
        }
      })
    }

    function toggleCategory(idx) {
      expanded[idx] = !expanded[idx]
    }

    function toggleChapter(idx) {
      expandedChapter[idx] = !expandedChapter[idx]
    }

    function getChapterNumber(category, chapter) {
      const idx = category.children.findIndex(c => c.idx === chapter.idx)
      return String(idx + 1).padStart(2, '0')
    }

    function getLessonStatus(lessonIdx) {
      return progressMap[lessonIdx]?.status || 'not_started'
    }

    function getLessonProgress(lessonIdx) {
      return progressMap[lessonIdx]?.progress || 0
    }

    function getCategoryProgress(category) {
      let total = 0
      let completed = 0
      
      category.children?.forEach(ch => {
        ch.children?.forEach(ls => {
          total++
          if (getLessonStatus(ls.idx) === 'completed') completed++
        })
      })

      return total > 0 ? Math.round((completed / total) * 100) : 0
    }

    function getCompletedLessons(chapter) {
      return chapter.children?.filter(ls => getLessonStatus(ls.idx) === 'completed').length || 0
    }

    const totalLessons = computed(() => allLessonsFlat.value.length)
    
    const completedCount = computed(() => 
      allLessonsFlat.value.filter(ls => getLessonStatus(ls.idx) === 'completed').length
    )

    const overallProgress = computed(() => 
      totalLessons.value > 0 ? Math.round((completedCount.value / totalLessons.value) * 100) : 0
    )

    function formatDuration(seconds) {
      const min = Math.floor(seconds / 60)
      if (min < 60) return `${min}분`
      const hour = Math.floor(min / 60)
      const remMin = min % 60
      return `${hour}시간 ${remMin}분`
    }

    function openLesson(lesson) {
      selectedLesson.value = lesson
      viewerVisible.value = true
    }

    function closeViewer() {
      viewerVisible.value = false
      selectedLesson.value = null
    }

    function handleProgressUpdate(lessonIdx, status, progress) {
      progressMap[lessonIdx] = { status, progress }
    }

    onMounted(() => {
      fetchData()
    })

    return {
      treeData,
      expanded,
      expandedChapter,
      toggleCategory,
      toggleChapter,
      getChapterNumber,
      getLessonStatus,
      getLessonProgress,
      getCategoryProgress,
      getCompletedLessons,
      totalLessons,
      completedCount,
      overallProgress,
      formatDuration,
      viewerVisible,
      selectedLesson,
      allLessonsFlat,
      openLesson,
      closeViewer,
      handleProgressUpdate,
      isAdmin,
      showAdmin,
      showHistory
    }
  }
}
</script>


<style scoped>
#dialog-curriculumList {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: column;
  background-color: var(--color-background);
  z-index: var(--z-index-middle);
}

.curriculum-list {
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.curriculum-list > .header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 60px;
  color: var(--color-text-primary);
  padding: 16px;
  box-sizing: border-box;
}

.curriculum-list > .header > p {
  aspect-ratio: 1/1;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--color-text-primary);
  font-size: var(--font-size-xl);
  cursor: pointer;
  margin: 0;
  padding: 0;
}

.curriculum-list > .header > h2 {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
  margin: 0;
  padding: 0;
}

.overall-progress {
  width: calc(100% - 32px);
  max-width: 768px;
  padding: 20px;
  margin: 0 16px 16px;
  background: var(--color-brand);
  border-radius: var(--radius-lg);
  box-sizing: border-box;
  color: var(--color-text-white);
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.progress-label {
  font-size: var(--font-size-sm);
  opacity: 0.9;
}

.progress-value {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
}

.progress-bar {
  height: 8px;
  background: rgba(255,255,255,0.3);
  border-radius: var(--radius-sm);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--color-text-white);
  border-radius: var(--radius-sm);
  transition: width 0.3s ease;
}

.progress-stats {
  margin-top: 8px;
  font-size: var(--font-size-sm);
  opacity: 0.8;
}

.content {
  width: 100%;
  max-width: 800px;
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
  box-sizing: border-box;
}

.category-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.category-section {
  background: var(--color-background-light);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.category-header:hover {
  background: var(--color-surface-reverse);
}

.category-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.category-info > i {
  font-size: var(--font-size-lg);
  color: var(--color-brand);
}

.category-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.category-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.category-progress {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-brand);
}

.category-meta > i {
  color: var(--color-text-secondary);
}

.chapter-list {
  border-top: 1px solid var(--color-background);
}

.chapter-section {
  border-bottom: 1px solid var(--color-background);
}

.chapter-section:last-child {
  border-bottom: none;
}

.chapter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  cursor: pointer;
  background: var(--color-surface);
}

.chapter-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chapter-number {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-secondary);
  background: var(--color-background);
  padding: 4px 8px;
  border-radius: var(--radius-sm);
}

.chapter-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.chapter-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.chapter-count {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.lesson-list {
  background: var(--color-background);
}

.lesson-item {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  gap: 12px;
  cursor: pointer;
  transition: background 0.15s ease;
  border-bottom: 1px solid var(--color-background-light);
}

.lesson-item:last-child {
  border-bottom: none;
}

.lesson-item:hover {
  background: var(--color-background-light);
}

.lesson-item.completed .lesson-status-icon {
  color: #22c55e;
}

.lesson-item.in-progress .lesson-status-icon {
  color: var(--color-brand);
}

.lesson-status-icon {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
  flex-shrink: 0;
}

.lesson-info {
  flex: 1;
  min-width: 0;
}

.lesson-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.type-icon {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
}

.lesson-title {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lesson-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
}

.lesson-duration,
.lesson-progress-text {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  gap: 4px;
}

.lesson-arrow {
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--color-text-secondary);
  gap: 12px;
}

.empty-state i {
  font-size: 48px;
  opacity: 0.5;
}
</style>